import React from 'react';
import { Grid, Box, Typography, Button } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { BlueBackgroundWrapper } from '../../components/blue-background-wrapper';
import { MessageForm } from './message-form';
import { ChatWindow } from './chat-window';
import { getBuildMessages, addBuildMessage } from '../../../../fetch/phases-of-contruction';
import { IBuildMessages } from '../../../../models/phases-of-contruction';
import { IResponse } from '../../../../models';
import { useBuilds } from '../../context';
import { useHistory } from 'react-router-dom';
import { HOMEOWNER_PORTAL_ROUTES } from '../../routes';

export const MessageBuildTeam: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedBuild, isLoading: isLoadingBuilds } = useBuilds();
  const history = useHistory();

  const {
    data: messagesResponse,
    isLoading: isLoadingMessages,
    error
  } = useQuery<IResponse<IBuildMessages[]>, Error>(
    ['getBuildMessages', selectedBuild?.buildId],
    () => getBuildMessages(selectedBuild?.buildId!),
    {
      enabled: !!selectedBuild?.buildId,
      refetchInterval: 60000 // Refetch every minute
    }
  );

  const isLoading = isLoadingBuilds || isLoadingMessages;

  // Submit message mutation
  const sendMessageMutation = useMutation(
    async (data: { file?: File; message: string }) => {
      const formData = new FormData();
      if (data.file) {
        formData.append('UploadedFile', data.file);
      }
      formData.append('Message', data.message);

      return await addBuildMessage(selectedBuild?.buildId!, formData);
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Message sent successfully', { variant: 'success' });
        // Invalidate the messages query cache to trigger a refresh
        queryClient.invalidateQueries(['getBuildMessages', selectedBuild?.buildId]);
      },
      onError: (error: any) => {
        enqueueSnackbar('Error sending message. Please try again.', { variant: 'error' });
      }
    }
  );

  const handleMessageSubmit = async (messageText: string, file?: File): Promise<void> => {
    if (!selectedBuild?.buildId) {
      enqueueSnackbar('Build ID not found', { variant: 'error' });
      return;
    }

    sendMessageMutation.mutate({
      file,
      message: messageText
    });
  };

  if (!selectedBuild?.buildId && !isLoading) {
    return (
      <BlueBackgroundWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="body1" color="text.secondary">
            Build information not available
          </Typography>
        </Box>
      </BlueBackgroundWrapper>
    );
  }

  if (isLoading) {
    return (
      <BlueBackgroundWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="body1" color="text.secondary">
            Loading messages...
          </Typography>
        </Box>
      </BlueBackgroundWrapper>
    );
  }

  if (error) {
    return (
      <BlueBackgroundWrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          sx={{ color: 'text.secondary' }}
        >
          <Typography variant="body1" color="text.secondary">
            Error loading messages. Please try again later.
          </Typography>
        </Box>
      </BlueBackgroundWrapper>
    );
  }

  const messages: IBuildMessages[] = (messagesResponse?.records as IBuildMessages[]) || [];

  return (
    <BlueBackgroundWrapper>
      <Grid container spacing={4} alignItems="stretch">
        {/* Left Side - Instructions and Form */}
        <Grid item xs={12} md={6}>
          {selectedBuild?.isInWarranty ? (
            <BlueBackgroundWrapper>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Congratulations on your new home! If you have a question on your home, please
                  contact our Warranty Team.
                </Typography>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Button
                    variant="contained"
                    onClick={() => {
                      history.push(HOMEOWNER_PORTAL_ROUTES.WARRANTY_PORTAL);
                    }}
                    sx={{ width: { xs: '100%', md: '275px' }, mt: 2 }}
                  >
                    Warranty Portal
                  </Button>
                </Box>
              </Box>
            </BlueBackgroundWrapper>
          ) : (
            <MessageForm onMessageSubmit={handleMessageSubmit} />
          )}
        </Grid>

        {/* Right Side - Chat Window */}
        <Grid item xs={12} md={6}>
          {messages.length > 0 ? (
            <ChatWindow messages={messages} />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100%'
              }}
            >
              <Typography variant="body1">There are no messages for this build.</Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </BlueBackgroundWrapper>
  );
};
